import { RowSelectionState } from "@tanstack/react-table";
import { RecognizeScheduleMilestoneBulkItem } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import { canMarkMilestoneAchieved } from "./milestoneRowHelpers";
import {
    isScheduleApplicationDrillDownRow,
    ScheduleApplicationParentRow,
    ScheduleApplicationPeriodRow,
    ScheduleApplicationRow,
} from "./scheduleApplicationRowHelpers";

export function isScheduleApplicationRow(
    value: unknown,
): value is ScheduleApplicationRow {
    return (
        typeof value === "object" &&
        value !== null &&
        "rowType" in value &&
        "id" in value
    );
}

/** Schedule rows always; of their ladder rows only pending milestones, behind the flag. */
export function canSelectScheduleApplicationRow(options: {
    row: ScheduleApplicationRow;
    isMilestoneEnabled: boolean;
}): boolean {
    const { row, isMilestoneEnabled } = options;
    if (!isScheduleApplicationDrillDownRow(row)) {
        return true;
    }
    return (
        isMilestoneEnabled &&
        row.rowType === "period" &&
        canMarkMilestoneAchieved(row)
    );
}

export interface SelectedMilestoneRow {
    parent: ScheduleApplicationParentRow;
    row: ScheduleApplicationPeriodRow;
}

export interface SelectedMilestoneRows {
    /** Each pending milestone row with the schedule row it sits under. */
    milestoneRows: SelectedMilestoneRow[];
    /** Selected ladder rows that aren't pending milestones; bulk confirm skips them. */
    skippedCount: number;
}

/**
 * The table selects schedule rows and, behind the milestone flag, their
 * pending milestone rows. Bulk "Milestone achieved" wants only the latter;
 * schedule rows feed the other bulk actions, so they aren't reported as
 * skipped here.
 */
export function getSelectedMilestoneRows(options: {
    rows: ScheduleApplicationParentRow[];
    rowSelection: RowSelectionState;
}): SelectedMilestoneRows {
    const { rows, rowSelection } = options;
    const milestoneRows: SelectedMilestoneRow[] = [];
    let skippedCount = 0;
    for (const parent of rows) {
        for (const row of parent.children) {
            if (!rowSelection[row.id]) {
                continue;
            }
            if (row.rowType === "period" && canMarkMilestoneAchieved(row)) {
                milestoneRows.push({ parent, row });
            } else {
                skippedCount += 1;
            }
        }
    }
    return { milestoneRows, skippedCount };
}

export interface BulkRecognizeRequest {
    entityId: number;
    milestones: RecognizeScheduleMilestoneBulkItem[];
}

/**
 * One request per entity, in selection order: the endpoint is entity-scoped.
 * Each row carries its own resolved date, so no batch date travels; a row
 * without one is sent dateless and posts on its expected date.
 */
export function buildBulkRecognizeRequests(options: {
    rows: Array<{
        entityId: number;
        applicationId: string;
        milestone: { sortOrder: number };
        postingDate?: string;
    }>;
}): BulkRecognizeRequest[] {
    const requests = new Map<number, BulkRecognizeRequest>();
    for (const row of options.rows) {
        const request = requests.get(row.entityId) ?? {
            entityId: row.entityId,
            milestones: [],
        };
        request.milestones.push({
            applicationId: row.applicationId,
            sortOrder: row.milestone.sortOrder,
            ...(row.postingDate ? { postingDate: row.postingDate } : {}),
        });
        requests.set(row.entityId, request);
    }
    return [...requests.values()];
}
